/**
 * Member Care — merged surface for the AI chat log + the care-rep ticket queue.
 *
 * Thin URL-driven tab wrapper: the active tab comes from the `:tab` route param
 * (/admin/care/chat | /admin/care/tickets), NOT local state, so each tab is
 * deep-linkable and the sidebar's pathname.startsWith('/admin/care') highlight
 * works. The two list components are rendered UNCHANGED — they own their own
 * local state + React Query keys, which don't collide. Ticket detail stays its
 * own route (/admin/tickets/:id); the queue's rows still link out to it.
 */
import { Navigate, useParams, Link } from 'react-router-dom';
import AdminSupportChats from './AdminSupportChats';
import AdminTicketQueue from './AdminTicketQueue';

const TABS = [
  { key: 'chat', label: 'Chat', to: '/admin/care/chat' },
  { key: 'tickets', label: 'Tickets', to: '/admin/care/tickets' },
] as const;

type CareTab = (typeof TABS)[number]['key'];

export default function AdminCare() {
  const { tab } = useParams<{ tab: string }>();

  // Unknown / missing tab → default to the chat tab.
  if (tab !== 'chat' && tab !== 'tickets') {
    return <Navigate to="/admin/care/chat" replace />;
  }
  const active: CareTab = tab;

  return (
    <div className="space-y-4">
      {/* Tab header — mirrors the admin pill-tab idiom (AdminSurveys) on the dark theme. */}
      <div className="flex items-center gap-2">
        {TABS.map((t) => {
          const isActive = t.key === active;
          return (
            <Link
              key={t.key}
              to={t.to}
              aria-current={isActive ? 'page' : undefined}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                isActive
                  ? 'bg-slate-700 text-white'
                  : 'text-slate-400 hover:text-white hover:bg-slate-800'
              }`}
            >
              {t.label}
            </Link>
          );
        })}
      </div>

      {active === 'chat' ? <AdminSupportChats /> : <AdminTicketQueue />}
    </div>
  );
}
